import { useState, useEffect, useCallback } from "react";
import { IonSpinner } from "@ionic/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faCheckCircle, faCrown } from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "../../contexts/AuthContext";
import { createPortalLinkFunc } from "../../lib/firebase/firebase";
import { getSubscriptionDetails } from "../../lib/stripe/getSubscriptionDetails";
import branding from "../../config/branding";
import PaywallModal from "../PaywallModal/PaywallModal";
import CancelFlowModal from "../CancelFlowModal/CancelFlowModal";
import "../SubscriptionPage/SubscriptionPage.css";

const formatDate = (date) => {
  if (!date) return "—";
  return new Date(date).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

const formatPrice = (priceData) => {
  if (!priceData) return null;
  const amount = priceData.unit_amount;
  if (amount == null) return null;
  return `$${(amount / 100).toFixed(2)}`;
};

const formatInterval = (priceData) => {
  if (!priceData?.recurring?.interval) return "";
  return priceData.recurring.interval === "month" ? "/month" : "/year";
};

const EmbroideryBillingPage = ({ onBack }) => {
  const { user, userIsEmbroideryPremium, refreshEmbroideryPremiumStatus } = useAuth();
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [details, setDetails] = useState(null);
  const [error, setError] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showCancelFlow, setShowCancelFlow] = useState(false);

  const fetchDetails = useCallback(async () => {
    if (!user?.uid) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getSubscriptionDetails(user.uid);
      // Find the EmbroideryTrace-specific subscription
      const embSub = data.allSubscriptions?.find(
        (s) =>
          s.role === "embroiderytrace" &&
          (s.status === "active" || s.status === "trialing" || s.cancelAtPeriodEnd)
      );
      setDetails({ ...data, embSub });
    } catch (err) {
      console.error("Failed to load embroidery subscription details:", err);
      setError("Couldn't load subscription details. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [user?.uid]);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  const handleManageBilling = async (changePlan = false) => {
    setPortalLoading(true);
    try {
      const { data } = await createPortalLinkFunc({ returnUrl: window.location.origin, action: changePlan === true ? "change_plan" : undefined });
      window.location.assign(data.url);
    } catch (err) {
      console.error("Error opening customer portal:", err);
      setError(err?.message || "Could not open billing. Please try again.");
      setPortalLoading(false);
    }
  };

  const isActive = !!userIsEmbroideryPremium;
  const sub = details?.embSub;
  const isCanceling = sub?.cancelAtPeriodEnd;
  const priceStr = formatPrice(sub?.price);
  const interval = formatInterval(sub?.price);
  const planName = sub?.product?.name || (interval === "/year" ? "Yearly" : "Monthly");

  return (
    <div className="sub-page">
      <div className="sub-page__container">
        <div className="sub-page__top-bar">
          <button className="sub-page__back-btn" onClick={onBack}>
            <FontAwesomeIcon icon={faArrowLeft} />
            <span>Back</span>
          </button>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "4px" }}>
          <img src={branding.logoIcon} alt={branding.appName} style={{ height: "32px", objectFit: "contain" }} />
          <h2 className="sub-page__title" style={{ margin: 0 }}>Billing</h2>
        </div>
        <p style={{ fontSize: "14px", color: "#64748b", fontFamily: "Clash Grotesk, sans-serif", marginBottom: "24px" }}>
          Manage your EmbroideryTrace membership
        </p>

        {loading ? (
          <div className="sub-page__loading">
            <IonSpinner name="crescent" style={{ width: 36, height: 36, color: "#d2349f" }} />
            <p>Loading your plan...</p>
          </div>
        ) : error ? (
          <div className="sub-page__error">
            <p>{error}</p>
            <button className="sub-page__btn sub-page__btn--primary" onClick={fetchDetails}>Retry</button>
          </div>
        ) : isActive && sub ? (
          /* Active subscription */
          <div className="sub-page__plan-card">
            <div className="sub-page__plan-header">
              <h3 className="sub-page__plan-name">
                EmbroideryTrace — {planName}
              </h3>
              <span className={`sub-page__badge ${isCanceling ? "sub-page__badge--warning" : "sub-page__badge--active"}`}>
                {isCanceling ? "Canceling" : "Active"}
              </span>
            </div>

            <div className="sub-page__detail-rows">
              {priceStr && (
                <div className="sub-page__detail-row">
                  <span className="sub-page__detail-label">Price</span>
                  <span className="sub-page__detail-value sub-page__detail-value--bold">
                    {priceStr}<span className="sub-page__detail-muted">{interval}</span>
                  </span>
                </div>
              )}
              {sub.currentPeriodEnd && (
                <div className="sub-page__detail-row">
                  <span className="sub-page__detail-label">
                    {isCanceling ? "Access until" : "Renews on"}
                  </span>
                  <span className="sub-page__detail-value">{formatDate(sub.currentPeriodEnd)}</span>
                </div>
              )}
            </div>

            {isCanceling && (
              <div className="sub-page__info-box sub-page__info-box--warning">
                Your membership ends on {formatDate(sub.currentPeriodEnd)}. You'll keep full access until then.
              </div>
            )}

            <button
              className="sub-page__btn sub-page__btn--primary"
              onClick={handleManageBilling}
              disabled={portalLoading}
              style={{ background: "linear-gradient(135deg, #f453c4, #d2349f)" }}
            >
              {portalLoading ? <IonSpinner name="lines" style={{ width: 20, height: 20 }} /> : "Manage Billing"}
            </button>
            <p className="sub-page__btn-hint">Update payment method or view invoices</p>
            {sub.status === "active" && !isCanceling && (
              <button className="sub-page__btn" onClick={() => handleManageBilling(true)} disabled={portalLoading}>Change monthly/yearly plan</button>
            )}

            {!isCanceling && (
              <button
                className="sub-page__btn"
                onClick={() => setShowCancelFlow(true)}
                style={{ background: "none", border: "1.5px solid #fecaca", color: "#dc2626", fontSize: 13, padding: "10px 16px" }}
              >
                Cancel Membership
              </button>
            )}
          </div>
        ) : (
          /* Free plan */
          <div className="sub-page__plan-card">
            <div className="sub-page__plan-header">
              <h3 className="sub-page__plan-name">Free Plan</h3>
              <span className="sub-page__badge sub-page__badge--neutral">Free</span>
            </div>

            <div className="sub-page__free-limits">
              <p className="sub-page__free-limits-title">Free plan includes:</p>
              <ul className="sub-page__free-list">
                <li>Full use of the Embroidery Editor</li>
                <li>Preview stitch files — no file export</li>
              </ul>
              <p className="sub-page__free-limits-title" style={{ marginTop: 12 }}>
                Unlock with a membership:
              </p>
              <ul className="sub-page__pro-list">
                {[
                  "Export DST, PES, JEF & more",
                  "Unlimited embroidery file exports",
                  "Auto Satin, Fill & Running Stitches",
                  "AI-Enhanced conversions",
                  "Commercial License",
                ].map((item) => (
                  <li key={item}>
                    <FontAwesomeIcon icon={faCheckCircle} style={{ color: "#0f7b74", marginRight: 6 }} />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <button
              className="sub-page__btn sub-page__btn--upgrade"
            onClick={() => setShowUpgradeModal(true)}
            style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}
          >
              <FontAwesomeIcon icon={faCrown} />
              Get EmbroideryTrace Membership
            </button>
          </div>
        )}

        <div className="sub-page__footer">
          <p>Need help? Contact <a href="mailto:support@embroiderytrace.com">support@embroiderytrace.com</a></p>
        </div>
      </div>

      {showUpgradeModal && (
        <PaywallModal
          isVisible={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          onSuccess={() => {
            setShowUpgradeModal(false);
            if (refreshEmbroideryPremiumStatus) refreshEmbroideryPremiumStatus();
            fetchDetails();
          }}
          action="export"
          isAuthenticated={!!user}
          isPremium={!!userIsEmbroideryPremium}
        />
      )}

      <CancelFlowModal
        isOpen={showCancelFlow}
        onClose={() => {
          setShowCancelFlow(false);
          fetchDetails();
        }}
        onCanceled={() => {
          fetchDetails();
          if (refreshEmbroideryPremiumStatus) refreshEmbroideryPremiumStatus();
        }}
        isIOSSub={false}
      />
    </div>
  );
};

export default EmbroideryBillingPage;
